import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TrendingUp, ArrowRight, Users, Clock, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const HeartsStakingCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staking, setStaking] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [email, setEmail] = useState('');
  const [hearts, setHearts] = useState(50);
  const [message, setMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [result, setResult] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await apiClient.request('/api/enhancements/hearts-stakes');
      setData(res);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleStake = async (e) => {
    e.preventDefault();
    setStaking(true);
    try {
      const res = await apiClient.request('/api/enhancements/hearts-stake', {
        method: 'POST',
        body: JSON.stringify({ target_email: email, hearts, message }),
      });
      setResult(res);
      setShowForm(false);
      setEmail(''); setHearts(50); setMessage('');
      await loadData();
      setTimeout(() => setResult(null), 4000);
    } catch (err) {
      alert(err.message || 'Failed to stake');
    } finally { setStaking(false); }
  };

  const handleResolve = async () => {
    setResolving(true);
    try {
      const res = await apiClient.request('/api/enhancements/hearts-stakes/resolve', { method: 'POST' });
      if (res.resolved > 0) {
        setResult({ message: `Resolved ${res.resolved} stake(s)! Check your balance.` });
        await loadData();
        setTimeout(() => setResult(null), 4000);
      }
    } catch {} finally { setResolving(false); }
  };

  const activeStakes = useMemo(() => data?.stakes_placed?.filter(s => s.status === 'active') || [], [data]);
  const pastStakes = useMemo(() => data?.stakes_placed?.filter(s => s.status !== 'active').slice(0, 3) || [], [data]);

  if (loading) {
    return (
      <div data-testid="hearts-staking-card" className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-40 mb-4" />
        <div className="h-24 bg-gray-100 rounded" />
      </div>
    );
  }

  return (
    <div data-testid="hearts-staking-card" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Hearts Staking</h3>
              <p className="text-xs text-gray-400">Invest in a friend&apos;s growth</p>
            </div>
          </div>
          <button
            data-testid="open-stake-form"
            onClick={() => setShowForm(!showForm)}
            className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors"
          >
            {showForm ? 'Cancel' : 'Stake Hearts'}
          </button>
        </div>

        {/* Result alert */}
        {result && (
          <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 font-medium">
            {result.message}
          </div>
        )}

        {/* Stats row */}
        {data && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-gray-900">{data.active_stakes_out}</p>
              <p className="text-xs text-gray-400">Active Out</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-gray-900">{data.active_stakes_in}</p>
              <p className="text-xs text-gray-400">Staked on You</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-indigo-600">{data.total_hearts_earned}</p>
              <p className="text-xs text-gray-400">Total Earned</p>
            </div>
          </div>
        )}

        {/* Stake form */}
        {showForm && (
          <form onSubmit={handleStake} className="space-y-3 p-4 bg-gray-50 rounded-xl mb-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Friend&apos;s Email</label>
              <input
                data-testid="stake-email-input"
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="friend@example.com" required
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Hearts to Stake (25-500)</label>
              <input
                data-testid="stake-hearts-input"
                type="number" value={hearts} onChange={e => setHearts(Number(e.target.value))}
                min={25} max={500} step={25} required
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <p className="text-xs text-gray-400 mt-1">If they complete 3 conversations this week, you both earn +{Math.round(hearts * 0.5)} bonus Hearts</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Message (optional)</label>
              <input
                type="text" value={message} onChange={e => setMessage(e.target.value)}
                placeholder="I believe in you!" maxLength={100}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <button
              data-testid="submit-stake"
              type="submit" disabled={staking}
              className="w-full py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {staking ? <><Loader2 className="w-4 h-4 animate-spin" /> Staking...</> : <><TrendingUp className="w-4 h-4" /> Stake {hearts} Hearts</>}
            </button>
          </form>
        )}

        {/* Active stakes list */}
        {data?.stakes_placed?.filter(s => s.status === 'active').length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-500 uppercase">Active Stakes</p>
              <button data-testid="resolve-stakes-btn" onClick={handleResolve} disabled={resolving}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50">
                {resolving ? 'Checking...' : 'Check Results'}
              </button>
            </div>
            {activeStakes.map(s => (
              <div key={s.stake_id} className="flex items-center gap-3 p-3 bg-indigo-50/50 rounded-xl">
                <Users className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{s.target_name || s.target_email}</p>
                  <p className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Expires {new Date(s.expires_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-indigo-600">{s.hearts}</p>
                  <p className="text-xs text-gray-400">staked</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Past results */}
        {pastStakes.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase">Past Stakes</p>
            {pastStakes.map(s => (
              <div key={s.stake_id} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50">
                {s.status === 'paid' ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
                <p className="text-sm text-gray-600 flex-1 truncate">{s.target_name}</p>
                <p className={`text-xs font-bold ${s.status === 'paid' ? 'text-emerald-600' : 'text-red-400'}`}>
                  {s.status === 'paid' ? `+${s.payout}` : `-${s.hearts}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeartsStakingCard;
